<script setup>
import { computed, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import HubShell from './components/shell/HubShell.vue';
import XlButton from './components/xl/XlButton.vue';
import XlModal from './components/xl/XlModal.vue';
import XlProgressLine from './components/xl/XlProgressLine.vue';
import XlSlowNotice from './components/xl/XlSlowNotice.vue';
import { pageLoad } from './helpers/pageLoad';
import { showSectionHome } from './composables/useUnsavedChanges';
import Notifications from './components/Notifications.vue';
import { getEnvURLs } from "./helpers/config.js";
import { isStaff } from './helpers/roles';
import { useHubPageTitle } from './composables/useHubPageTitle';
import { useAuthStore } from './stores/auth.js';
import Clarity from "@microsoft/clarity";

const notification = ref({
  visible: false
});
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const subdomain = window.store.subdomain;

// The hub used to keep the last page in a 30-day last_visited_page cookie that
// nothing reads, and a page URL can carry a sign-in secret (XN-4561). It is no
// longer written; the one a browser may still hold is expired, on the domain
// and path it was set with.
document.cookie = `last_visited_page=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${window.location.host}; path=/`;
const store = window.store;

// The Hub shell, shown to every staff role — brokers included. This used to
// compare against the bare string 'attendee', which is precisely how a fourth
// role ends up rendering the wrong chrome. Computed, not a snapshot, so it
// follows a role change without a reload.
const showAdminChrome = computed(
  () => !!authStore.userRole && isStaff(authStore.userRole) && !route.meta.hideNav
);

// One title, read by the top bar and by document.title alike.
const hubPageTitle = useHubPageTitle();

const envURLs = getEnvURLs();
provide('envURLs', envURLs);
window.loadNotification = (args) => {
  notification.value = { visible: true, ...args };
};

// No Clarity id (local stacks) means no Clarity: init('') still loads the tag and 404s.
if (!import.meta.env.VITE_IS_LOCAL && import.meta.env.VITE_CLARITY_ID) {
  Clarity.init(import.meta.env.VITE_CLARITY_ID);
}

// The hub's name is a watch source, not a value captured at startup: it used
// to be read once, so renaming the hub in Settings left the browser tab
// showing the old name until a reload. window.store is reactive, so writing
// store.subdomainName after a save now retitles the tab straight away.
watch([() => route.name, () => store.subdomainName], () => {
  // `immediate` runs this before the first navigation resolves, when `matched`
  // is still empty — and a path outside the route table never fills it.
  store.currentRoute = router.currentRoute.value.matched[0] ?? null;

  if (subdomain && store.currentRoute?.path) {
    if (store.subdomainName) {
      document.title = `${store.subdomainName} ${hubPageTitle.value}`;
    }
  }
}, { immediate: true });

// Leave without saving: the changes go, and so does the person. From the
// sidebar's own section (goToSectionHome), the editor open in place closes
// first, then its main page shows.
const handleChangeRoute = () => {
  store.historyChanges = [];
  store.changeRouteDialog.show = false;
  if (store.changeRouteDialog.sectionHome) {
    void showSectionHome(router, store.changeRouteDialog.toRoute);
    return;
  }
  if (!store.changeRouteDialog.external) {
    router.push(store.changeRouteDialog.toRoute);
    return;
  }
  window.location = store.changeRouteDialog.toRoute;
};

// Keep editing: nothing moves, and everything entered is still there.
const keepEditing = () => {
  store.changeRouteDialog.show = false;
};

// A page that can save itself registers how (window.store.saveCurrentPage), and
// gets a third button. Without one the dialog is the two it has always been —
// leaving is still only ever Discard or Cancel, never a silent save.
const canSaveHere = computed(() => typeof store.saveCurrentPage === 'function');
const savingBeforeLeave = ref(false);

const handleSaveAndLeave = async () => {
  savingBeforeLeave.value = true;
  try {
    // A refused save keeps the dialog open: navigating away on a failure is
    // exactly the data loss the prompt exists to prevent.
    if (await store.saveCurrentPage()) handleChangeRoute();
  } finally {
    savingBeforeLeave.value = false;
  }
};
</script>

<template>
  <div>
    <!-- Loading (helpers/pageLoad): the line at the top while a page is on
         its way; the shell around the page is drawn at once and never waits.
         `retryKey` mounts just the page again when Retry asks for it. -->
    <XlProgressLine />

    <HubShell v-if="showAdminChrome">
      <router-view v-slot="{ Component }">
        <component :is="Component" :key="pageLoad.retryKey" />
      </router-view>
    </HubShell>

    <main v-else data-page-slot>
      <XlSlowNotice />
      <router-view v-slot="{ Component }">
        <component :is="Component" :key="pageLoad.retryKey" />
      </router-view>
    </main>

    <Notifications :args="notification" @close="notification.visible = false" />

    <!-- Shown only when something was actually changed (useUnsavedChanges).
         The prototype's short confirm (`fDelModal`): the question as the
         heading, what it means under it, then Keep editing — the default, so
         an Enter never throws work away — and the destructive answer last.
         Northstar, as every Hub page is: App.vue sits outside them. -->
    <div v-if="store.changeRouteDialog.show" data-xl-gen="northstar">
      <XlModal
        title="Leave without saving?"
        subtitle="You have unsaved changes."
        max-width="430px"
        data-testid="unsaved-changes"
        @close="keepEditing"
      >
        <template #actions>
          <XlButton variant="ghost" size="sm" autofocus data-testid="unsaved-keep" @click="keepEditing">Keep editing</XlButton>
          <XlButton
            v-if="canSaveHere"
            variant="ghost"
            size="sm"
            :loading="savingBeforeLeave"
            data-testid="unsaved-save"
            @click="handleSaveAndLeave"
          >Save and leave</XlButton>
          <XlButton variant="danger" size="sm" data-testid="unsaved-leave" @click="handleChangeRoute">Leave without saving</XlButton>
        </template>
      </XlModal>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.success-modal-content {
  width: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  align-items: center;
}

.success-modal-actions {
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
